import {
  Color,
  EntityReference,
  IAddress,
  IAmount,
  IArticleKeyEntityReference,
  IArticleKeyPrice,
  IBasket,
  IBasketItem,
  ICurrencyEntityReference,
  IEntityReferenceWithDisplayName,
  IEntityReferenceWithKey,
  IFAQ,
  IMediaAndPhotos,
  IMethod,
  IPhotoEntityReference,
  ISides,
  ITeamMember,
  IUiButtonSettings,
  Mode,
  PictureDisplayMode,
  ResponsiveAttribute,
  TransactionType
} from '@gs/shared';
import { ArticleGroupType } from 'libs/shared/src/models/articles/article';
import { IArticleGuarantee } from 'libs/shared/src/models/articles/article-guarantee';
import { IBrand } from 'libs/shared/src/models/articles/brand';
import { ITeaser } from 'libs/shared/src/models/teaser/teaser';
import { IDialog } from '../../basic/dialog';
import {
  GsStructBorderRadius,
  GsStructBorderStyle,
  GsStructBorderWidth,
  GsStructComponent,
  GsStructPageItems,
  GsStructSwiper
} from '../../component';
import { IArticleRatingsItem } from '../article-ratings-component';

export enum ThumbnailsPosition {
  LEFT = 0,
  BOTTOM = 1
}

/**
 * Display mode for the master/slave variant groups.
 * `buttons` renders every value of a group as an individual selectable button,
 * `dropdown` renders the group's values inside a single select-dropdown.
 */
export type SlavePropertyDisplayMode = 'buttons' | 'dropdown';

/**
 * How unavailable (not deliverable) variants are handled in the variant dropdowns.
 * `normal` renders them like any other value, `hide` removes them from the list,
 * `grayout` shows them disabled (struck-through, not selectable).
 */
export type UnavailableVariantDisplayMode = 'normal' | 'hide' | 'grayout';

/**
 * Which buy button the PDP shows. `auto` shows every button whose page exists (checkout and/or inquiry checkout),
 * `checkout` only the add-to-cart button, `inquiry` only the add-to-inquiry-list button.
 */
export type PurchaseButtonMode = 'auto' | 'checkout' | 'inquiry';

export interface GsStructArticleInfoBase<TRender extends IArticleInfoBaseRender>
  extends GsStructComponent<TRender>, GsStructSwiper, GsStructPageItems {
  /** Type of the component */
  componentType: 'ArticleInfo' | 'ArticleInfoCemetery' | 'ArticleInfoTicket' | 'ArticleInfoVoucher' | 'ArticleInfoRollLawn';
  /** Content text */
  content: string;
  /** ID of the article */
  articleID: string;
  /** ID of the article key */
  articleKeyID: string;
  /** Position of the thumbnails (left or bottom) */
  thumbnailsPosition: ThumbnailsPosition;
  /** Padding for the items */
  itemPadding: ISides;
  /** Margin for the items */
  itemMargin: ISides;
  /** Border color for the item image */
  borderColorItemImage?: Color;
  /** Border width for the item image */
  borderWidthItemImage?: GsStructBorderWidth;
  /** Border style for the item image */
  borderStyleItemImage?: GsStructBorderStyle;
  /** Border radius for the item image */
  borderRadiusItemImage?: GsStructBorderRadius;
  /** Border radius for the swiper thumbnail images (responsive) */
  borderRadiusSwiperImage?: ResponsiveAttribute<GsStructBorderRadius> | GsStructBorderRadius;
  /** Segment Tabs List  */
  segmentBars: ISegmentTab[];
  /** Voucher Min Value(price) */
  voucherMinValue?: number;
  /** Voucher Max Value(price) */
  voucherMaxValue?: number;
  /** Show "Add to Wishlist" button on the mobile bottom modal (near the Add to Cart button) */
  showAddToWishlistButton: boolean;
  /** Whether to enable the zoom functionality */
  zoomable: boolean;
  /** The zoom factor */
  zoomFactor: number;
  /** The number of days for the "New" badge */
  newForDays: number;
  /** The height of the image in the gallery */
  imageHeight: ResponsiveAttribute<number>;
  /** Button styling settings */
  buttons?: IUiButtonSettings;
  /** Display mode for the main preview image. */
  previewPictureDisplayMode?: PictureDisplayMode;
  /** Display mode for the thumbnail-swiper images. */
  swiperPictureDisplayMode?: PictureDisplayMode;
  /** Should the Article-References section be displayed? */
  showReferences?: boolean;
  /** Should the Watchlist button inside the Article-References-Items be displayed? */
  referencesShowWatchlistButton?: boolean;
  /** Display mode for the ratings tab (grid or slider) */
  ratingsMode?: Mode;
  /** Should the availability stock details be displayed? */
  showAvailabilityDetails?: boolean;
  /** Should the tiered prices (Staffelpreise) be displayed below the variant? */
  showTieredPrices?: boolean;
  /** Hides the transaction-type (Lieferart) selection; the preselection stays active in the background */
  hideShippingMethod?: boolean;
  /** Should the link to the quality/variant explanation page be displayed? (default: true) */
  showQualityExplanation?: boolean;
  /** Display mode for the master/slave variant groups (individual buttons or a dropdown) */
  slavePropertyDisplayMode?: SlavePropertyDisplayMode;
  /** How unavailable variants are handled in the variant dropdowns (normal, hidden or grayed-out) */
  unavailableVariantDisplayMode?: UnavailableVariantDisplayMode;
  /** Should the "notify me when available again" form be displayed for unavailable variants? */
  showAvailabilityNotification?: boolean;
  /** Which buy button is displayed: both (auto), only add-to-cart or only add-to-inquiry-list (default: auto) */
  purchaseButtonMode?: PurchaseButtonMode;
}

export interface IArticleInfoBaseRender {
  /** The main article information object */
  article?: IArticleData;
  /** The URL for shipping costs information */
  shippingCostsURL: string;
  /** quality and Explanation URL */
  qualityExplanation: string;
  /** The media and photo assets associated with the article */
  mediaAndPhotos?: IMediaAndPhotos[];
  /** The initial selected article key id */
  articleKey?: EntityReference;
  /** True if articleKey was requested via the link (akid), false if it is only the backend's default key */
  articleKeyPreselected?: boolean;
  /** Is the watchlist configured to be used in the webshop? */
  webshopSettingsWatchlist: boolean;
  /** The average rating value for the article */
  articleRatingAverage?: number;
  /** The total number of ratings for the article */
  articleRatingCount?: number;
  /** The textual representation of the article's rating */
  articleRatingText?: string;
  /** The properties of the article wich are set by the user */
  articleProperties: IArticleProperty[];
  /** The pictos associated with the article (displayed in description-tab) */
  articlePictos?: IArticleInfoPicto[];
  /**
   * The possible slave properties for the article
   *  @description Will display button-groups instead of variant-selection
   */
  slaveProperties?: ISlaveProperty[];
  /** The segments wich should get listed at the bottom of the article info */
  segmentBars: ISegmentTab[];
  /** Should the button to add the article to the wishlist get shown? */
  showAddToWishlistButton: boolean;
  /** Mthods for different tasks on the API */
  methods: IArticleInfoBaseMethods;
  /** The personalisation dialog of the currently selected article key, replaced on every key change */
  dialog?: IDialog;
  /** Relative link to the article detail page, for components that pin their article via `articleID` */
  detailLink?: string;
}

export interface IArticleData {
  articleGroups: IEntityReferenceWithDisplayName[];
  articleID: number;
  botanicName?: string;
  createdOn?: Date;
  /** The Botanical Latin name */
  name: string;
  /** The common name */
  name2?: string;
  description: string;
  shortDescription: string;
  categories?: IEntityReferenceWithDisplayName[];
  brand?: IBrand;
  keys?: IArticleKey[];
  texts?: IArticleText[];
  bloomingTimeFrom?: number;
  bloomingTimePeriod?: ITimePeriod;
  bloomingTimePeriod2?: ITimePeriod;
  bloomingTimeTo?: number;
  teaser?: ITeaser;
  teamMember?: ITeamMember;
  /** The references to other articles, which get displayed in the article info component */
  references?: IArticleReference[];
  /** Article ratings (delivered inline with article) */
  ratings?: IArticleRatingsItem[];
  /** Total number of ratings for the article */
  ratingCount?: number;
  /** Herstellergarantie zur Haltbarkeit (EmpCo-Garantie-Label) — `null`, wenn keine anzeigepflichtige Garantie vorliegt */
  guarantee?: IArticleGuarantee | null;
}

/**
 * Slave-properties are used to define button-groups on an article for alternative key selection
 * @example Color could be a slave-property and would get displayed as a group, the values would then represent the buttons inside
 */
export interface ISlaveProperty {
  /** The title of the slave property */
  title: string;
  /** The article property associated with the slave property */
  articleProperty: IEntityReferenceWithKey;
  /**
   * The priority configured on the field for ordering the variant groups (ascending, 1 = highest).
   * The backend serializes this sort-ready: fields without a priority are delivered as a large value so
   * that ordering the groups by `priority` ascending (via the orderBy pipe) puts them last, while the
   * backend's alphabetical tie-break keeps them alphabetical.
   */
  priority?: number;
  /** The possible values for the slave property */
  values: ISlaveValue[];
}

/**
 * The values for the slave properties, which represent the buttons in the button-group, with the article key they are associated with
 */
export interface ISlaveValue {
  /** The title of the slave value */
  title: string;
  /** The color associated with the slave value */
  color?: string;
  /** TODO: Describe this attribute */
  key?: string;
  /** The article key associated with the slave value / gets set if selected */
  articleKey?: EntityReference;
  /** Is this key selected by default inside the group? */
  selected?: boolean;
  /** Is this value enabled or disabled (disabled values get shown but are not selectable) */
  enabled?: boolean;
}

export interface IArticleProperty {
  /** The type of the article property */
  type: TextType;
  /** The key of the article-property */
  key: string;
  /** The value to display for the user */
  displayValue: string;
  /** The key/title of the property */
  displayTitle: string;
  /** The sorting priority of the property */
  priority: number;
}

export interface IArticleInfoPicto {
  /** The name of the picto */
  name: string;
  /** The text associated with the picto */
  text: string;
  /** The unique key of the picto */
  key: string;
  /** The photo of the picto */
  photo: IPhotoEntityReference;
}

export interface IArticleInfoBaseMethods {
  /** Adds the article-id to the basket */
  addToCheckout: IMethod;
  /** Adds the article-id to the inquiry list (RequestList basket) — present only when inquiry checkout is available */
  addToInquiry?: IMethod;
  /** Adds the article-id to the watchlist */
  addToWatchList: IMethod;
  /** Removes the article-id from the watchlist */
  deleteFromWatchlist: IMethod;
  /** Loads the informations for a key of this article-id */
  articleKeyInfo: IMethod;
  /** Loads all FAQs for this article-id */
  fAQs: IMethod;
  /** Loads all ratings for this article-id */
  articleRatings?: IMethod;
  /** Loads the full article-info render data for a referenced article (used by the reference quick-buy modal) */
  articleReferenceInfos?: IMethod;
  /** Member Info */
  memberInfo?: IMethod;
  /** Registers an e-mail address for the "article available again" notification */
  subscribeAvailability?: IMethod;
  /** Loads the selectable shipping, delivery and pickup methods incl. costs for an article key */
  shippingMethods?: IMethod;
}

/** The response of the getShippingMethods Method */
export interface IArticleInfoGetShippingMethodsResponse {
  /** Selectable shipping, delivery and pickup methods of the article key */
  shippingMethods: IArticleKeyShippingMethod[];
}

/** A shipping, delivery or pickup method selectable on the product detail page */
export interface IArticleKeyShippingMethod {
  /** ID of the shipping method, undefined for download */
  shippingMethodID?: number;
  /** The transaction type the method belongs to */
  transactionType: TransactionType;
  /** Name of the method, e.g. "Schweizer Post" */
  name: string;
  /** Delivery time; an article specific delivery time overrides the one of the method */
  estimatedShippingDuration?: string;
  /** Additional description of the method */
  description?: string;
  /** Logo of the method */
  picture?: IPhotoEntityReference;
  /** Whether it is the default method of its transaction type */
  default: boolean;
  /** Costs for this article; for radius delivery the lowest ("from") price */
  price?: IAmount;
  /** Basket value from which the costs drop to zero */
  freeShippingFrom?: IAmount;
  /** Link to the shipping cost calculator, only for radius delivery whose price depends on the address */
  linkTarget?: string;
}

/** The response of the getFAQs Method */
export interface IArticleInfoGetFAQsResponse {
  fAQs: IFAQ[];
}

/** The response of the getRatings Method */
export interface IArticleInfoGetRatingsResponse {
  ratings: IArticleRatingsItem[];
}

/** All the possible types of content inside the segments underneath the article gallery and price-info */
export type ContentType =
  | 'details'
  | 'description'
  | 'texts'
  | 'detailsWithPictograms'
  | 'pictoGrid'
  | 'appointments'
  | 'purchaseArguments'
  | 'articleProperties'
  | 'faq'
  | 'courseDetails'
  | 'instructor'
  | 'ratings';

export interface ISegmentTab {
  /** Tab's name */
  name: string;
  /** Tab's type */
  contentType: ContentType;
  /** Tab's sorting index */
  sortIndex: number;
}

/**
 * The interface wich holds information about an article key.
 * Used for the current selection of keys in the article info component and to change the key.
 */
export interface IArticleRenderKey {
  /** The ID of the article key */
  articleKey: EntityReference;
  /** All possible chainstores wich are offering this key */
  articleKeysChainStores: IArticleKeysChainstores[];
  /** Can this key be baught? */
  canBuyKey: boolean;
  /** The time needed until delivery */
  shippingDeliverTime: string;
  /** The shown name of the key */
  value: string;
  /** The properties of the article wich are set by the user */
  articleProperties: IArticleProperty[];
  /** Possible transaction types of this key */
  transactionTypes: IArticleKeyTransactionType[];
  /** Medias and photos from the article-key */
  mediaAndPhotos?: IMediaAndPhotos[];
  /** Available stock quantity for the current article key */
  availableStockQuantity?: number;
  /** The width of the roll lawn in meters */
  rollLawnWidthMeters?: number;
}

/** Information about a transaction type of an article key */
export interface IArticleKeyTransactionType {
  /** The type of transaction */
  transactionType: TransactionType;
  /** The icon to show in the selection and as info. */
  icon: string;
  /** A longer title describing the transaction-type more */
  text: string;
  /** A shorter title of the transaction-type */
  title: string;
  /** Additional shipping text, e.g. delivery times */
  shippingText?: string;
  /** Some link to a different site. */
  linkTarget?: string;
}

/** The full article key information */
export interface IArticleKey {
  articleKeyID: number;
  basePriceInfoText?: string;
  info: string;
  value: string;
  supplierValue?: string;
  createdOn?: Date;
  decimals?: number;
  packingUnit?: string;
  packingSize?: string;
  minimumOrderQuantity?: number;
  packingUnitType?: string;
  packingForm?: string;
  deliverSize?: string;
  deliverSize2?: string;
  deliverUnitType?: string;
  deliverType?: string;
  deliverType2?: string;
  deliverUnitType2?: string;
  stockQuantity?: number;
  handlingTime?: string;
  storageLocation?: string;
  signage?: string;
  personalizable: boolean;
  eAN: string;
  article: { iD: number };
  country?: string;
  taxRate: { iD: number };
  category: { iD: number };
  category2?: { iD: number };
  categories: { iD: number }[];
  teaser?: ITeaser;
  availableForClickAndCollect: boolean;
  availableForDropshipping: boolean;
  availableForMarketPlaces: boolean;
  availableForRadiusDelivery: boolean;
  availableForDownload: boolean;
  availableForShipping: boolean;
  availableForShippingText?: string;
  availableForShippingDeliverTime?: string;
  availableForRadiusDeliveryText?: string;
  availableForRadiusDeliveryDeliverTime?: string;
  availableForClickAndCollectText?: string;
  availableForClickAndCollectDeliverTime?: string;
  growthFrom?: number;
  growthTo?: number;
  weightFrom?: number;
  weightTo?: number;
  widthFrom?: number;
  widthTo?: number;
  heightFrom?: number;
  heightTo?: number;
  floorSpaceFrom?: number;
  floorSpaceTo?: number;
  circumferenceFrom?: number;
  circumferenceTo?: number;
  stemHeightFrom?: number;
  stemHeightTo?: number;
  deliverHeightFrom?: number;
  deliverHeightTo?: number;
  lengthFrom?: number;
  lengthTo?: number;
  depthFrom?: number;
  depthTo?: number;
  potSize?: string;
  potSizeL?: string;
  fillAmountFrom?: number;
  fillAmountTo?: number;
  diameterFrom?: number;
  diameterTo?: number;
  loadingCapacityFrom?: number;
  loadingCapacityTo?: number;
  bloomingTimeFrom?: number;
  bloomingTimeTo?: number;
  bloomingTimePeriod?: ITimePeriod;
  priority: number;
  size?: string;
  quality?: string;
  description?: string;
  teamMember?: ITeamMember;
  features: { iD: number }[];
  tasks: {
    articleTaskID: number;
    task: { iD: number };
    fromMonth: number;
    toMonth: number;
  }[];
  prices: IArticleKeyPrice[];
  texts: {
    articleTextID: number;
    position: number;
    type: number;
    title: string;
    value: string;
    displayTitle: string;
  }[];
  ratings: unknown[];
  tags: { iD: number }[];
  photos: IPhotoEntityReference[];
  /** Indicates that this article key is currently not available (true =  unavailable for purchase) */
  notAvailable: boolean;
}

export interface IArticleText {
  articleTextID: number;
  position: number;
  type: number;
  title: string;
  value: string;
  displayTitle: string;
}

export interface ITimePeriod {
  timePeriodID: number;
  fromMonth: number;
  toMonth: number;
}

export enum TextType {
  Location = 0,
  Hardiness = 1,
  OptimalSummerTemperature = 2,
  OptimalWinterTemperature = 3,
  Flower = 4,
  FlowerColor = 5,
  FlowerTime = 6,
  FlowerSmell = 7,
  Growth = 8,
  GrowthHeight = 9,
  GrowthWidth = 10,
  GrowthAnnualGrowth = 11,
  GrowthSpeed = 12,
  GrowthShape = 13,
  Water = 14,
  Pruning = 15,
  PruningFromTo = 16,
  Fruit = 17,
  FruitCone = 18,
  FruitRipe = 19,
  WaterDepth = 20,
  Seed = 21,
  Harvest = 22,
  Sowing = 23,
  Availibility = 24,
  Foliage = 25,
  FoliageColor = 26,
  FoliageAutumnColor = 27,
  AlwaysGreen = 28,
  Soil = 29,
  Synonym = 30,
  Description = 31,
  PlantPartners = 32,
  SimilarPlants = 33,
  Usages = 34,
  TrademarkProtection = 35,
  PlantVarietyProtection = 36,
  LocaleSynonym = 37,
  Origins = 38,
  Tasks = 39,
  LeaveColor = 40,
  SeedDepth = 41,
  SeedPlantDistance = 42,
  SeedRowDistance = 43,
  SeedGerminationDays = 44,
  SeedGerminationTemperature = 45,
  SeedTransplant = 46,
  SeedGermination = 47,
  SeedDistance = 48,
  LifeTime = 49,
  Other = 50,
  Adr = 51,
  SeedPlanting = 52,
  Substances = 53,
  Care = 54,
  WorthKnowing = 55,
  Specialty = 56,
  Attributes = 57,
  Advice = 58,
  Hibernation = 59,
  Claim = 60,
  Reproduction = 61,
  Bark = 62,
  Wood = 63,
  Needling = 64,
  FreeText6 = 65,
  Output = 66,
  WateringFertilizing = 67,
  Fertilizing = 68,
  Substrate = 69,
  WaterLevel = 70,
  RipeTime = 71,
  Stipe = 72,
  Cut = 73,
  MedicalBenefit = 74,
  Taste = 75,
  Variety = 76,
  Impregnation = 77,
  Tree = 78,
  PlantingTime = 79,
  Planting = 80,
  Compost = 81,
  Difficulty = 82,
  PlantingDistance = 83,
  FreeText1 = 84,
  FreeText2 = 85,
  FreeText3 = 86,
  FreeText4 = 87,
  FreeText5 = 88,
  OriginCountry = 89,
  Durability = 90,
  StemLength = 91,
  Tipp = 92,
  Sustainability = 93,
  Leave = 94,
  Grower = 95,
  Stem = 96,
  FreeText7 = 97,
  FreeText8 = 98,
  FreeText9 = 99,
  FreeText10 = 100,
  FreeText11 = 101,
  FreeText12 = 102,
  Size = 103,
  Smell = 104,
  Opener = 105,
  FreeText13 = 106,
  FreeText14 = 107,
  FreeText15 = 108,
  FreeText16 = 109,
  FreeText17 = 110,
  FreeText18 = 111,
  FreeText19 = 112,
  FreeText20 = 113,
  Health = 114,
  Pulp = 115,
  Picking = 116,
  Stalk = 117,
  FlowerDiameter = 118,
  Culm = 119,
  FreeText21 = 120,
  BulletPoints = 121,
  Recommendation = 122,
  Composition = 123,
  Material = 124,
  NlBulletPoints = 125,
  Contact = 126,
  Spreading = 127,
  PlantForm = 128,
  BulbSize = 129
}

/**
 * The Reference to an article
 * @description Can be used to display related articles in the article info component.
 */
export interface IArticleReference {
  /** The referenced article (id) */
  article?: EntityReference;
  /** The referenced article key */
  articleKey?: IArticleKeyEntityReference;
  /** The ID of the article reference */
  articleReferenceID?: number;
  /** The type of the article reference */
  articleReferenceType?: number;
  /** Quantity available for the referenced article key */
  availableStockQuantity?: number;
  /** The base price information text */
  basePriceInfoText?: string;
  /** Indicates if the article key can be purchased */
  canBuyKey?: boolean;
  /** The currency of the article */
  currency?: ICurrencyEntityReference;
  /** The detail link of the article */
  detailLink?: string;
  /** The main photo of the article */
  mainPhoto?: IPhotoEntityReference;
  /** The price information of the article */
  priceInfo?: IArticlePriceInfoDto;
  /** The priority for sorting the article references */
  priority?: number;
  /** The subtitle of the article */
  subTitle?: string;
  /** The title of the article */
  title?: string;
  /** The transaction types available for the article key */
  transactionTypes?: IArticleKeyTransactionType[];
  /** Whether the referenced article-key is currently in the user's watchlist */
  articleKeyInWatchlist?: boolean;
  /** The article group types (e.g. Ticket for events). Used to drive reference behavior (event references link to the detail page instead of offering add-to-cart). */
  articleGroupTypes?: ArticleGroupType[];
}

/** DTO for article price information */
interface IArticlePriceInfoDto {
  minKeyPrice?: IAmount;
  minKeyOldPrice?: IAmount;
  priceCount?: number;
  info?: string;
  countKeyOldPrices?: number;
  countKeyPrices?: number;
  from?: boolean;
  fromPrice?: boolean;
  fromOldPrice?: boolean;
}

export interface IArticleKeyInfoResponse {
  /** The article key information */
  articleKey: IArticleRenderKey;
  /** Is the key currently in the watchlist of the user? */
  articleKeyInWatchlist: boolean;
  /**
   * The fixed price of the watchlist row of this key, for articles priced freely (vouchers). The amount is
   * part of the identity of a watchlist row, so the page has to know it to remove that row again.
   */
  watchlistFixedPrice?: number;
  /** The updated possible slave properties for the article after an article key change */
  slaveProperties?: ISlaveProperty[];
  /**
   * The personalisation dialog of the newly selected article key. Each variant can carry its own signage
   * template with its own element GUIDs, so the form has to be rebuilt from it (WLG-295).
   */
  dialog?: IDialog;
}

/** The response returned after adding an article key to the watchlist or removing it */
export interface IUpdateWatchListResponse extends IArticleKeyInfoResponse {
  /** The updated basket after adding the article key to the watchlist */
  basket: IBasket;
  /** The specific basket item corresponding to the added article key */
  basketItem: IBasketItem;
}

export interface IArticleKeysChainstores {
  address: IAddress;
  chainStoreID: number;
  eMailAddress: string;
  phone: string;
  webSite: string;
}
